import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import questionApi from 'api/questionApi';
import { sanitizeHtml } from 'helpers';
import { extractKnowledgeCheckIds, readKnowledgeCheckIds } from './markup';
import { hydrateInteractiveWidgets } from './hydrate';
import KnowledgeCheckHydrate from './KnowledgeCheckHydrate';
import styles from './mc-widgets.module.scss';

const InteractiveHtml = ({ html, questions = [], className, style }) => {
  const containerRef = useRef(null);
  const [portals, setPortals] = useState([]);
  const [fetchedQuestions, setFetchedQuestions] = useState([]);

  const sanitized = useMemo(
    () => (typeof html === 'string' ? sanitizeHtml(html) : ''),
    [html],
  );

  const idsFromHtml = useMemo(
    () => extractKnowledgeCheckIds(sanitized),
    [sanitized],
  );

  useEffect(() => {
    const missing = idsFromHtml.filter(
      (id) => !questions.some((question) => String(question?._id) === String(id)),
    );
    if (!missing.length) {
      setFetchedQuestions([]);
      return undefined;
    }
    let cancelled = false;
    Promise.all(
      missing.map((id) =>
        questionApi.getById({ id }).catch(() => null),
      ),
    ).then((results) => {
      if (!cancelled) {
        setFetchedQuestions(results.filter(Boolean));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [idsFromHtml, questions]);

  const allQuestions = useMemo(() => {
    const map = new Map();
    [...questions, ...fetchedQuestions].forEach((question) => {
      if (question?._id) {
        map.set(String(question._id), question);
      }
    });
    return [...map.values()];
  }, [questions, fetchedQuestions]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) {
      return undefined;
    }
    hydrateInteractiveWidgets(root);
    const nodes = [
      ...root.querySelectorAll(
        '.mc-knowledge-check, [data-mc-widget="knowledge-check"]',
      ),
    ];
    const nextPortals = nodes.map((node, index) => ({
      node,
      key: `${node.getAttribute('data-question-ids') || 'kc'}-${index}`,
      ids: readKnowledgeCheckIds(node),
    }));
    nodes.forEach((node) => {
      node.innerHTML = '';
    });
    setPortals(nextPortals);
    return undefined;
  }, [sanitized]);

  if (!sanitized) {
    return <div />;
  }

  return (
    <>
      <div
        ref={containerRef}
        className={[styles.root, className].filter(Boolean).join(' ')}
        style={style}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
      {portals.map((portal) =>
        createPortal(
          <KnowledgeCheckHydrate
            questionIds={portal.ids}
            questions={allQuestions}
          />,
          portal.node,
        ),
      )}
    </>
  );
};

export default InteractiveHtml;
